/**
 * MessageInput Component
 *
 * Message composition input with send functionality.
 */

import React, { useState, useRef, useEffect } from 'react';

interface MessageInputProps {
  onSend: (message: string) => void;
  isSending: boolean;
  placeholder?: string;
  // Optional controlled value: when provided, the parent owns the message
  // text. Used so quick-reply chips can pre-fill the textarea.
  value?: string;
  onChange?: (value: string) => void;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  isSending,
  placeholder = 'Type a message...',
  value,
  onChange,
}) => {
  const isControlled = typeof value === 'string' && typeof onChange === 'function';
  const [internalMessage, setInternalMessage] = useState('');
  const message = isControlled ? value! : internalMessage;
  const setMessage = (v: string) => {
    if (isControlled) onChange!(v);
    else setInternalMessage(v);
  };
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, [message]);

  const handleSubmit = () => {
    const trimmed = message.trim();
    if (trimmed && !isSending) {
      onSend(trimmed);
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t bg-white p-4">
      <div className="flex items-end gap-3">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={isSending}
          />
          <p className="text-xs text-gray-400 mt-1">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
        <button
          onClick={handleSubmit}
          disabled={!message.trim() || isSending}
          className="px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSending ? (
            <>
              <SpinnerIcon className="w-5 h-5 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <SendIcon className="w-5 h-5" />
              Send
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Icons
const SendIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
  </svg>
);

const SpinnerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export default MessageInput;
